import { useState, useEffect } from "react";
import { 
  Search, 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  ExternalLink,
  Save,
  Loader2,
  X,
  ChevronUp,
  ChevronDown,
  Globe,
  FileText,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";
import SectionEditor from '../sections/SectionEditor';
import SectionStudio from '../sections/SectionStudio';
const { createSection, isPremiumSection, validationErrors } = require('../sections/catalog.cjs');

// Section type labels in Swedish
const SECTION_LABELS = {
  hero: "Hero (toppsektion)",
  trust_bar: "Trust bar (förtroende)",
  testimonials: "Omdömen",
  lead_form: "Formulär (leadgenerering)",
  text: "Text",
  benefits: "Fördelar",
  faq: "Vanliga frågor (FAQ)",
  cta: "Call to Action",
  image_text: "Bild + Text",
  pricing_preview: "Pris-förhandsvisning"
};

export default function SeoPagesEditor() {
  const { getAuthHeaders } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPage, setNewPage] = useState({ title: "", slug: "", meta_title: "", meta_description: "" });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sectionTemplates, setSectionTemplates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showStudio, setShowStudio] = useState(false);

  useEffect(() => {
    loadPages();
    loadTemplates();
  }, []);

  // Filter pages based on search query
  const filteredPages = pages.filter(page => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      page.title?.toLowerCase().includes(query) ||
      page.slug?.toLowerCase().includes(query) ||
      page.meta_title?.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    loadPages();
    loadTemplates();
  }, []);

  const loadPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seo-pages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error("Failed to load pages:", error);
      toast.error("Kunde inte ladda sidor");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seo-pages-admin/section-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSectionTemplates(data.templates || {});
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    }
  };

  const seedDefaultPages = async () => {
    if (!window.confirm("Detta skapar 5 standard SEO-sidor med svenskt innehåll. Befintliga sidor med samma slug kommer att hoppas över. Vill du fortsätta?")) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/seo-pages-admin/seed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.created.length > 0) {
          toast.success(`Skapade ${data.created.length} nya sidor!`);
          loadPages();
        } else {
          toast.info("Alla sidor finns redan");
        }
      } else {
        throw new Error("Seed failed");
      }
    } catch (error) {
      console.error("Seed error:", error);
      toast.error("Kunde inte importera sidor");
    } finally {
      setSeeding(false);
    }
  };

  const createPage = async (e) => {
    e.preventDefault();
    if (!newPage.title || !newPage.slug) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/seo-pages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(newPage)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Sidan skapades!");
        setShowCreateModal(false);
        setNewPage({ title: "", slug: "", meta_title: "", meta_description: "" });
        loadPages();
        setSelectedPage(data.page);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Kunde inte skapa sidan");
      }
    } catch (error) {
      toast.error("Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const savePage = async () => {
    if (!selectedPage) return;
    if (selectedPage.status === 'published') {
      const errors = (selectedPage.sections || []).flatMap(validationErrors);
      if (errors.length) { toast.error(errors[0]); return; }
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/seo-pages/${selectedPage.slug}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          title: selectedPage.title,
          meta_title: selectedPage.meta_title,
          meta_description: selectedPage.meta_description,
          status: selectedPage.status,
          sections: selectedPage.sections
        })
      });

      if (response.ok) {
        toast.success("Sidan sparades!");
        loadPages();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Kunde inte spara");
      }
    } catch (error) {
      toast.error("Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (slug) => {
    if (!window.confirm("Är du säker på att du vill radera denna sida?")) return;

    try {
      const response = await fetch(`${API_URL}/api/seo-pages/${slug}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Sidan raderades!");
        if (selectedPage?.slug === slug) setSelectedPage(null);
        loadPages();
      }
    } catch (error) {
      toast.error("Kunde inte radera");
    }
  };

  const addSection = (type) => {
    if (!selectedPage || (!createSection(type) && !sectionTemplates[type])) return;
    
    const newSection = createSection(type) || JSON.parse(JSON.stringify(sectionTemplates[type]));
    setSelectedPage(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
  };

  const removeSection = (index) => {
    setSelectedPage(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedPage.sections.length) return;
    
    const sections = [...selectedPage.sections];
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    setSelectedPage(prev => ({ ...prev, sections }));
  };

  const updateSection = (index, field, value) => {
    setSelectedPage(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const updateSectionItem = (sectionIndex, itemIndex, field, value) => {
    setSelectedPage(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const items = [...(s.items || [])];
        items[itemIndex] = { ...items[itemIndex], [field]: value };
        return { ...s, items };
      })
    }));
  };

  const addSectionItem = (sectionIndex, template) => {
    setSelectedPage(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        return { ...s, items: [...(s.items || []), template] };
      })
    }));
  };

  const removeSectionItem = (sectionIndex, itemIndex) => {
    setSelectedPage(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        return { ...s, items: s.items.filter((_, j) => j !== itemIndex) };
      })
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button type="button" onClick={() => setShowStudio(!showStudio)} className="px-4 py-2.5 rounded-lg border border-[#255e69] text-[#255e69] text-sm font-medium">{showStudio ? 'Stäng sektionsbiblioteket' : 'Öppna sektionsbiblioteket · 9 designer'}</button>
      {showStudio && <SectionStudio />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Search className="w-6 sm:w-7 h-6 sm:h-7 text-[#255E69]" />
            SEO-sidor
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Skapa och redigera SEO-landningssidor.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {pages.length === 0 && (
            <button
              onClick={seedDefaultPages}
              disabled={seeding}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Importera standard-sidor
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#255E69] text-white rounded-xl hover:bg-[#1e4a53] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Ny sida
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pages List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Sidor ({pages.length})</h2>
            </div>
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Sök på titel eller slug..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]/20 focus:border-[#255E69]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-gray-500">
                Visar {filteredPages.length} av {pages.length} sidor
              </p>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {filteredPages.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {searchQuery ? "Inga sidor matchar sökningen." : "Inga sidor ännu. Skapa din första!"}
              </div>
            ) : (
              filteredPages.map((page) => (
                <div
                  key={page.slug}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedPage?.slug === page.slug ? "bg-[#255E69]/5 border-l-2 border-[#255E69]" : ""
                  }`}
                  onClick={() => setSelectedPage(page)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{page.title}</h3>
                      <p className="text-sm text-gray-500">/{page.slug}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      page.status === "published" 
                        ? "bg-green-100 text-green-700" 
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {page.status === "published" ? "Publicerad" : "Utkast"}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <a
                      href={`/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#255E69] hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Visa
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); deletePage(page.slug); }}
                      className="text-xs text-red-500 hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Radera
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Page Editor */}
        <div className="lg:col-span-2">
          {selectedPage ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Editor Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Redigera: {selectedPage.title}</h2>
                <div className="flex gap-2">
                  <select
                    value={selectedPage.status}
                    onChange={(e) => setSelectedPage(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="draft">Utkast</option>
                    <option value="published">Publicerad</option>
                  </select>
                  <button
                    onClick={savePage}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-1.5 bg-[#255E69] text-white rounded-lg text-sm hover:bg-[#1e4a53] disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Spara
                  </button>
                </div>
              </div>

              {/* Meta fields */}
              <div className="p-4 border-b border-gray-100 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                    <input
                      type="text"
                      value={selectedPage.title}
                      onChange={(e) => setSelectedPage(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL (slug)</label>
                    <div className="flex items-center">
                      <span className="text-gray-400 text-sm mr-1">/</span>
                      <input
                        type="text"
                        value={selectedPage.slug}
                        onChange={(e) => setSelectedPage(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title (SEO)</label>
                  <input
                    type="text"
                    value={selectedPage.meta_title}
                    onChange={(e) => setSelectedPage(prev => ({ ...prev, meta_title: e.target.value }))}
                    placeholder="Sidtitel | Elido"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description (SEO)</label>
                  <textarea
                    value={selectedPage.meta_description}
                    onChange={(e) => setSelectedPage(prev => ({ ...prev, meta_description: e.target.value }))}
                    placeholder="Kort beskrivning för sökmotorer..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Sections */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Sektioner</h3>
                  <div className="flex gap-2">
                    <select
                      onChange={(e) => { if (e.target.value) { addSection(e.target.value); e.target.value = ""; }}}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                      defaultValue=""
                    >
                      <option value="" disabled>+ Lägg till sektion</option>
                      {Object.entries(SECTION_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sections List */}
                <div className="space-y-4">
                  {(selectedPage.sections || []).length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                      Inga sektioner ännu. Lägg till din första sektion ovan.
                    </div>
                  ) : (
                    selectedPage.sections.map((section, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Section Header */}
                        <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-700">
                            {SECTION_LABELS[section.type] || section.type}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveSection(index, -1)}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => moveSection(index, 1)}
                              disabled={index === selectedPage.sections.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeSection(index)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Section Content Editor */}
                        <div className="p-4 space-y-3">
                          <SectionEditor section={section} onChange={next => setSelectedPage(prev => ({ ...prev, sections: prev.sections.map((s, i) => i === index ? next : s) }))} />
                          {/* Hero section */}
                          {section.type === "hero" && !isPremiumSection(section) && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.headline || ""}
                                  onChange={(e) => updateSection(index, "headline", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Underrubrik</label>
                                <textarea
                                  value={section.subheadline || ""}
                                  onChange={(e) => updateSection(index, "subheadline", e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Primär CTA Text</label>
                                  <input
                                    type="text"
                                    value={section.cta_primary?.text || ""}
                                    onChange={(e) => updateSection(index, "cta_primary", { ...section.cta_primary, text: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Primär CTA Länk</label>
                                  <input
                                    type="text"
                                    value={section.cta_primary?.link || ""}
                                    onChange={(e) => updateSection(index, "cta_primary", { ...section.cta_primary, link: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Sekundär CTA Text</label>
                                  <input
                                    type="text"
                                    value={section.cta_secondary?.text || ""}
                                    onChange={(e) => updateSection(index, "cta_secondary", { ...section.cta_secondary, text: e.target.value })}
                                    placeholder="Lämna tom för att dölja"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Sekundär CTA Länk</label>
                                  <input
                                    type="text"
                                    value={section.cta_secondary?.link || ""}
                                    onChange={(e) => updateSection(index, "cta_secondary", { ...section.cta_secondary, link: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Hero-bild (URL)</label>
                                <input
                                  type="text"
                                  value={section.hero_image || ""}
                                  onChange={(e) => updateSection(index, "hero_image", e.target.value)}
                                  placeholder="https://example.com/bild.jpg"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                                <p className="text-xs text-gray-400 mt-1">Visas till höger om texten på desktop, under texten på mobil</p>
                                {section.hero_image && (
                                  <div className="mt-2">
                                    <img 
                                      src={section.hero_image} 
                                      alt="Hero preview" 
                                      className="w-32 h-20 object-cover rounded-lg border"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* Text section */}
                          {section.type === "text" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Innehåll</label>
                                <textarea
                                  value={section.content || ""}
                                  onChange={(e) => updateSection(index, "content", e.target.value)}
                                  rows={6}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                                />
                              </div>
                            </>
                          )}

                          {/* Benefits section */}
                          {section.type === "benefits" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Fördelar</label>
                                <div className="space-y-2">
                                  {(section.items || []).map((item, i) => (
                                    <div key={i} className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Titel"
                                        value={item.title || ""}
                                        onChange={(e) => updateSectionItem(index, i, "title", e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      />
                                      <input
                                        type="text"
                                        placeholder="Beskrivning"
                                        value={item.description || ""}
                                        onChange={(e) => updateSectionItem(index, i, "description", e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      />
                                      <button
                                        onClick={() => removeSectionItem(index, i)}
                                        className="p-2 text-red-400 hover:text-red-600"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => addSectionItem(index, { title: "", description: "", icon: "check" })}
                                    className="text-sm text-[#255E69] hover:underline"
                                  >
                                    + Lägg till fördel
                                  </button>
                                </div>
                              </div>
                            </>
                          )}

                          {/* FAQ section */}
                          {section.type === "faq" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Frågor & Svar</label>
                                <div className="space-y-2">
                                  {(section.items || []).map((item, i) => (
                                    <div key={i} className="p-3 bg-gray-50 rounded-lg space-y-2">
                                      <input
                                        type="text"
                                        placeholder="Fråga"
                                        value={item.question || ""}
                                        onChange={(e) => updateSectionItem(index, i, "question", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      />
                                      <textarea
                                        placeholder="Svar"
                                        value={item.answer || ""}
                                        onChange={(e) => updateSectionItem(index, i, "answer", e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      />
                                      <button
                                        onClick={() => removeSectionItem(index, i)}
                                        className="text-xs text-red-500 hover:underline"
                                      >
                                        Ta bort
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => addSectionItem(index, { question: "", answer: "" })}
                                    className="text-sm text-[#255E69] hover:underline"
                                  >
                                    + Lägg till fråga
                                  </button>
                                </div>
                              </div>
                            </>
                          )}

                          {/* CTA section */}
                          {section.type === "cta" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Underrubrik</label>
                                <input
                                  type="text"
                                  value={section.subtitle || ""}
                                  onChange={(e) => updateSection(index, "subtitle", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Knapp-text</label>
                                  <input
                                    type="text"
                                    value={section.cta_text || ""}
                                    onChange={(e) => updateSection(index, "cta_text", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Knapp-länk</label>
                                  <input
                                    type="text"
                                    value={section.cta_link || ""}
                                    onChange={(e) => updateSection(index, "cta_link", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {/* Image + Text section */}
                          {section.type === "image_text" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Innehåll</label>
                                <textarea
                                  value={section.content || ""}
                                  onChange={(e) => updateSection(index, "content", e.target.value)}
                                  rows={4}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Bild-URL</label>
                                <input
                                  type="text"
                                  value={section.image || ""}
                                  onChange={(e) => updateSection(index, "image", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Bild Alt-text</label>
                                <input
                                  type="text"
                                  value={section.image_alt || ""}
                                  onChange={(e) => updateSection(index, "image_alt", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Bild-position</label>
                                <select
                                  value={section.image_position || "right"}
                                  onChange={(e) => updateSection(index, "image_position", e.target.value)}
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                >
                                  <option value="left">Vänster</option>
                                  <option value="right">Höger</option>
                                </select>
                              </div>
                            </>
                          )}

                          {/* Pricing preview */}
                          {section.type === "pricing_preview" && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rubrik</label>
                                <input
                                  type="text"
                                  value={section.title || ""}
                                  onChange={(e) => updateSection(index, "title", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Underrubrik</label>
                                <input
                                  type="text"
                                  value={section.subtitle || ""}
                                  onChange={(e) => updateSection(index, "subtitle", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Välj en sida</h3>
              <p className="text-gray-500 text-sm">
                Välj en sida från listan till vänster för att redigera den.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Skapa ny SEO-sida</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={createPage} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={newPage.title}
                  onChange={(e) => setNewPage(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Hemsida för småföretag"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL (slug)</label>
                <div className="flex items-center">
                  <span className="text-gray-400 mr-1">/</span>
                  <input
                    type="text"
                    value={newPage.slug}
                    onChange={(e) => setNewPage(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    placeholder="hemsida-smaforetag"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                <input
                  type="text"
                  value={newPage.meta_title}
                  onChange={(e) => setNewPage(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="Hemsida för småföretag | Elido"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea
                  value={newPage.meta_description}
                  onChange={(e) => setNewPage(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="Beskrivning för sökmotorer..."
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1e4a53] disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Skapa sida
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

